import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Certificate, MagnifyingGlass, SpinnerGap } from '@phosphor-icons/react';
import { internshipAPI } from '../../services/api';
import { applyPageSeo } from '../../utils/seo';

const pretty = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const CertificateSearch = () => {
  const [q, setQ] = useState('');
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    return applyPageSeo({
      title: 'Internship Certificates | Verify Letters | Growmore IT Services',
      description:
        'Search and verify Growmore IT Services internship offer and completion letters issued from Satara.',
      keywords:
        'Growmore internship certificate, verify internship letter Satara, internship completion letter',
      path: '/certificates'
    });
  }, []);

  const search = async (event) => {
    event.preventDefault();
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await internshipAPI.searchLetters(q);
      setLetters(data.letters || []);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-sky-50 dark:from-dark-900 dark:via-dark-900 dark:to-dark-800">
      <div className="absolute -top-16 right-10 w-64 h-64 bg-brand-400/15 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-24 left-8 w-40 h-40 bg-violet-400/10 rounded-full blur-3xl" />

      <section className="relative pt-28 pb-10">
        <div className="section-max-width px-4 md:px-6">
          <p className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full badge-primary mb-4 text-[11px] font-semibold uppercase tracking-[0.14em]">
            <Certificate className="w-4 h-4" weight="fill" /> Official letters
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight max-w-2xl">
            Find and download a <span className="text-gradient">certificate</span>
          </h1>
          <p className="text-base text-gray-600 dark:text-gray-300 leading-7 mt-3 max-w-2xl">
            Search by name, intern ID, letter ID or date of birth. Online and offline letters both open here, including after a QR scan.
          </p>

          <form onSubmit={search} className="mt-8 rounded-3xl bg-white/90 dark:bg-dark-800/90 border border-gray-100 dark:border-gray-800 shadow-soft p-3 sm:p-4 flex flex-col sm:flex-row gap-3">
            <label className="relative flex-1">
              <MagnifyingGlass className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 shrink-0" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-2xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-dark-800 py-3.5 pl-12 pr-4 text-base text-gray-900 dark:text-white placeholder:text-gray-400 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                placeholder="Name / GMIS-INT- / GMIS-OL- / 2004-05-12"
              />
            </label>
            <button type="submit" className="btn-primary text-white px-6 py-3.5 inline-flex items-center justify-center gap-2 shrink-0">
              {loading ? <SpinnerGap className="w-5 h-5 animate-spin" /> : <MagnifyingGlass className="w-5 h-5" />}
              Search
            </button>
          </form>
        </div>
      </section>

      <section className="relative pb-20">
        <div className="section-max-width px-4 md:px-6">
          {!searched && (
            <div className="rounded-3xl border border-dashed border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-dark-800/40 p-10 text-center">
              <Certificate className="w-10 h-10 text-brand-500 mx-auto mb-3" weight="duotone" />
              <p className="font-display font-bold text-gray-900 dark:text-white">Ready when you are</p>
              <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                Enter a name or letter ID. Open the result to print or save as PDF.
              </p>
            </div>
          )}

          {searched && !loading && letters.length === 0 && (
            <p className="text-center text-sm text-gray-500 py-10">No letter found. Try intern ID, letter ID or date of birth.</p>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            {letters.map((item) => (
              <Link
                key={item.letter_code}
                to={`/verify/${item.letter_code}`}
                className="group rounded-3xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 p-5 shadow-soft hover:-translate-y-1 hover:border-brand-300 hover:shadow-xl smooth-transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-brand-50 text-brand-700 px-2.5 py-1 rounded-md">
                    {item.type === 'completion' ? 'Completion' : 'Offer'} letter
                  </span>
                  <ArrowRight className="w-4 h-4 text-brand-600 opacity-0 group-hover:opacity-100 translate-x-0 group-hover:translate-x-0.5 smooth-transition" />
                </div>
                <p className="font-display text-xl font-extrabold text-gray-900 dark:text-white mt-3">{item.intern_name}</p>
                <p className="text-sm text-gray-500 mt-1">{item.role_title}</p>
                <p className="font-mono text-xs font-semibold text-brand-700 mt-3">{item.letter_code}</p>
                <p className="text-xs text-gray-400 mt-2">Issued {pretty(item.issued_at)} · Start {pretty(item.join_date)}</p>
                <p className="text-sm font-semibold text-brand-600 mt-4 inline-flex items-center gap-1">
                  Open / download <ArrowRight className="w-3.5 h-3.5" />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CertificateSearch;
